import React from 'react'
import { createUseStyles } from 'react-jss'
import { useSelector } from 'react-redux'
import { getProducts } from '../redux/slices/content'
import RelatedProductCard from './RelatedProductCard'
import theme from '../style/theme'

const RelatedProducts = ({ productId, limit = 3, manualProducts = [], title = 'Tastes great with...' }) => {
  const classes = useStyles()
  const allProducts = useSelector(getProducts)
  // console.log('RelatedProducts: allProducts', allProducts)
  // console.log('🔗 RelatedProducts: Received productId:', productId)
  // console.log('🔗 RelatedProducts: Manual products:', manualProducts)

  // Debug: Check if we can find the current product in the list
  if (allProducts && productId) {
    // const currentProduct = allProducts.find(p => p._id === productId || p.id === productId)
    // console.log('🔗 RelatedProducts: Found current product:', currentProduct || 'NOT FOUND')
  }

  if (!allProducts) {
    return (
      <div className={classes.container}>
        <h2 className={classes.title}>{title}</h2>
        <div className={classes.grid}>
          {[...Array(limit)].map((_, i) => (
            <div key={i} className={classes.skeleton} />
          ))}
        </div>
      </div>
    )
  }

  let products = []

  // If manual products are provided, use them first
  if (manualProducts && manualProducts.length > 0) {
    // Map manual product references to actual product data
    const manualProductData = manualProducts
      .map(productRef => {
        if (!productRef) return null

        // Handle different reference formats
        const productRefId = productRef._id || productRef.id || productRef._ref
        if (!productRefId) return null

        // Find the actual product data
        return allProducts.find(product =>
          product._id === productRefId ||
          product.id === productRefId ||
          product._ref === productRefId
        )
      })
      .filter(Boolean) // Remove null/undefined entries

    products = [...manualProductData]
  }

  // If we don't have enough products from manual selection, fill with automatic suggestions
  if (products.length < limit) {
    const remainingSlots = limit - products.length
    const usedProductIds = new Set([
      productId,
      ...products.map(p => p._id || p.id)
    ].filter(Boolean))

    const automaticProducts = allProducts
      .filter(product => {
        // Exclude already selected products
        const currentProductId = product._id || product.id
        if (usedProductIds.has(currentProductId)) return false

        // Check if product is in stock (active status)
        const isInStock = product.store?.status === 'active'

        // Check if product title contains "test" (case-insensitive)
        const productTitle = product.store?.title || product.title || ''
        const containsTest = productTitle.toLowerCase().includes('test')

        // Multiple ways to check if this is the current product (for backward compatibility)
        const productIds = [product._id, product.id].filter(Boolean)
        const isNotCurrentProduct = !productIds.some(id =>
          id === productId ||
          id.includes(productId) ||
          productId.includes(id)
        )

        const shouldInclude = isNotCurrentProduct && isInStock && !containsTest

        // console.log('🔗 RelatedProducts: Product filter check:', {
        //   productIds,
        //   currentProductId: productId,
        //   productTitle,
        //   isInStock,
        //   containsTest,
        //   isNotCurrentProduct,
        //   shouldInclude
        // })
        return shouldInclude
      })
      .slice(0, remainingSlots)

    products = [...products, ...automaticProducts]
  }

  // Final limit enforcement
  products = products.slice(0, limit)

  // console.log('🔗 RelatedProducts: Filtered products:', products.length)

  if (!products.length) {
    return null
  }

  return (
    <div className={classes.container}>
      <h2 className={classes.title}>{title}</h2>
      <div className={classes.grid}>
        {products.map((product) => (
          <RelatedProductCard
            key={product._id || product.id}
            product={product}
            imageMaxHeightClass={classes.relatedProductImageMaxHeight}
          />
        ))}
      </div>
    </div>
  )
}

const useStyles = createUseStyles({
  container: {
    marginTop: '150px',
    marginBottom: '108px',
    maxWidth: 'calc(100% - 9rem)',
    margin: '0 auto',
    [theme.breakpoints.down('md')]: {
      marginTop: '110px',
      marginBottom: '58px'
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      paddingLeft: '20px',
      paddingRight: '20px'
    }
  },
  title: {
    fontSize: '48px',
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    marginBottom: '60px',
    color: theme.colors.primary,
    textAlign: 'left',
    [theme.breakpoints.down('md')]: {
      fontSize: '30px',
      marginBottom: '40px'
    }

  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'auto auto auto',
    gap: '20px',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
      gap: '19px'
    }
  },
  relatedProductImageMaxHeight: {
    maxHeight: '331px'
  },
  skeleton: {
    height: '400px',
    backgroundColor: theme.colors.lightGrey,
    borderRadius: '8px',
    animation: '$pulse 1.5s ease-in-out infinite'
  },
  '@keyframes pulse': {
    '0%': { opacity: 0.6 },
    '50%': { opacity: 0.8 },
    '100%': { opacity: 0.6 }
  }
}, { name: 'RelatedProducts' })

export default RelatedProducts
